import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import {
  clearMindmapDocument,
  clearMindmapHighlight,
  setMindmapDocument,
  setMindmapHighlight,
  setMindmapLocationBounds,
} from '../../features/mindmap/mindmapSlice';
import { setWorkspaceView, toggleMindmapDrawerExtended } from '../../features/ui/uiSlice';
import {
  collectExpandableIds,
  collectMachineMarkers,
  collectSubtreeIds,
  collectWorkCells,
  computeLocationBounds,
  defaultExpandedIds,
  expandPathToNode,
  filterTree,
  findNodeById,
  hasMindmapFilters,
  MACHINE_PREVIEW_LIMIT,
  nodeTypeLabel,
} from '../../utils/processMachineMindmap';
import type { ProcessMachineNode, ProcessMachineNodeType } from '../../types/processMachineMap';
import { parseProcessMachineMapDocument } from '../../utils/processMachineMapUpload';
import { fetchProcessMachineMap, type ProcessMachineMapSource } from '../../utils/dxfApi';

const FILTER_TYPES: { id: ProcessMachineNodeType; label: string }[] = [
  { id: 'process', label: 'Process' },
  { id: 'subprocess', label: 'Subprocess' },
  { id: 'tool_group', label: 'Tool group' },
  { id: 'machine', label: 'Machine' },
];

const TYPE_META: Record<
  string,
  { accent: string; badge: string; iconBg: string; icon: string }
> = {
  fab: {
    accent: 'bg-slate-700',
    badge: 'bg-slate-800 text-slate-100',
    iconBg: 'bg-slate-800 text-amber-300',
    icon: 'F',
  },
  process: {
    accent: 'bg-sky-500',
    badge: 'bg-sky-50 text-sky-700 border border-sky-100',
    iconBg: 'bg-sky-100 text-sky-700',
    icon: 'P',
  },
  subprocess: {
    accent: 'bg-teal-500',
    badge: 'bg-teal-50 text-teal-700 border border-teal-100',
    iconBg: 'bg-teal-100 text-teal-700',
    icon: 'S',
  },
  tool_group: {
    accent: 'bg-cyan-600',
    badge: 'bg-cyan-50 text-cyan-800 border border-cyan-100',
    iconBg: 'bg-cyan-100 text-cyan-800',
    icon: 'T',
  },
  machine: {
    accent: 'bg-slate-400',
    badge: 'bg-slate-50 text-slate-500 border border-slate-200',
    iconBg: 'bg-slate-100 text-slate-500',
    icon: 'M',
  },
};

const DEFAULT_TYPE_META = {
  accent: 'bg-slate-400',
  badge: 'bg-slate-100 text-slate-600 border border-slate-200',
  iconBg: 'bg-slate-100 text-slate-500',
  icon: '·',
};

// The LLM categorization runs in the background after import. The mind map fetches only when it is
// opened (or reloaded): it prefers the LLM result, and until that is ready it shows the process one.
const llmHasData = (payload: unknown): boolean =>
  ((payload as { summary?: { total_machines?: number } } | null)?.summary?.total_machines ?? 0) > 0;

const ProcessMachineMindmap: React.FC = () => {
  const dispatch = useAppDispatch();
  const doc = useAppSelector((s) => s.mindmap.document);
  const fileName = useAppSelector((s) => s.mindmap.fileName);
  const floorPlanId = useAppSelector((s) => s.floorplan.floorPlanId);
  const relatedIds = useAppSelector((s) => s.mindmap.relatedIds);
  const highlightNodeId = useAppSelector((s) => s.mindmap.nodeId);
  const relatedSet = useMemo(() => new Set(relatedIds), [relatedIds]);

  const [loadError, setLoadError] = useState<string | null>(null);
  const [loadingSource, setLoadingSource] = useState<ProcessMachineMapSource | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [filterTypes, setFilterTypes] = useState<Set<ProcessMachineNodeType>>(new Set());
  const [filterWorkCell, setFilterWorkCell] = useState<string>('');
  const [filterOnMapOnly, setFilterOnMapOnly] = useState(false);
  const [drawerCollapsed, setDrawerCollapsed] = useState(false);
  const [drawerTab, setDrawerTab] = useState<'tree' | 'details'>('tree');
  const drawerExtended = useAppSelector((s) => s.ui.mindmapDrawerExtended);
  const initializedDocId = useRef<string | null>(null);

  const busy = loadingSource !== null;
  const canLoadFromApi = Boolean(floorPlanId);

  const applySelection = useCallback(
    (node: ProcessMachineNode, root: ProcessMachineNode) => {
      setSelectedId(node.id);
      setExpanded((prev) => expandPathToNode(root, node.id, prev));

      const related = collectSubtreeIds(node);
      // Skip flooding the floor map when the entire fab is selected.
      const { markers, machineCount } =
        node.type === 'fab'
          ? { markers: [], machineCount: node.count ?? collectMachineMarkers(node).machineCount }
          : collectMachineMarkers(node);

      dispatch(
        setMindmapHighlight({
          nodeId: node.id,
          nodeName: node.name,
          nodeType: node.type,
          relatedIds: related,
          markers,
          machineCount,
        })
      );
    },
    [dispatch]
  );

  // When a document is loaded, seed expand once — no map highlight until click.
  useEffect(() => {
    if (!doc) {
      initializedDocId.current = null;
      setSelectedId(null);
      setExpanded(new Set());
      return;
    }
    const docKey = `${doc.floor_plan_id}:${doc.name}`;
    if (initializedDocId.current === docKey) return;
    initializedDocId.current = docKey;
    setQuery('');
    setFilterTypes(new Set());
    setFilterWorkCell('');
    setFilterOnMapOnly(false);
    setSelectedId(null);
    setExpanded(defaultExpandedIds(doc.root));
    dispatch(setMindmapLocationBounds(computeLocationBounds(doc.root)));
    dispatch(clearMindmapHighlight());
  }, [doc, dispatch]);

  // Clear floor markers whenever the mindmap drawer unmounts.
  useEffect(() => {
    return () => {
      dispatch(clearMindmapHighlight());
    };
  }, [dispatch]);

  const applyPayload = useCallback(
    (payload: unknown, source: ProcessMachineMapSource) => {
      const data = parseProcessMachineMapDocument(payload);
      dispatch(
        setMindmapDocument({ document: data, fileName: `process-machine-map (${source})` })
      );
    },
    [dispatch]
  );

  // One fetch per open/reload — no background polling. Prefer the LLM categorization; while it's still
  // being built in the background, show the immediate process (regex) result instead. Re-opening the
  // mind map re-checks whether the LLM result has become ready.
  const handleLoadMindmap = useCallback(async () => {
    if (!floorPlanId) {
      setLoadError('Open a backend floor plan first — the mind map needs a floor_plan_id.');
      return;
    }
    setLoadError(null);
    setLoadingSource('regex');
    try {
      let llm: unknown = null;
      try {
        llm = await fetchProcessMachineMap({ floorPlanId, source: 'llm' });
      } catch {
        llm = null; // LLM endpoint hiccup -> fall back to the process result
      }
      if (llmHasData(llm)) {
        applyPayload(llm, 'llm'); // LLM ready -> show ONLY the LLM categorization
        return;
      }
      const regex = await fetchProcessMachineMap({ floorPlanId, source: 'regex' });
      applyPayload(regex, 'regex'); // LLM still processing -> show the process result for now
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoadingSource(null);
    }
  }, [floorPlanId, applyPayload]);

  // Fetch only when the mind map opens (mount) or the floor plan changes. Re-fetch while still on the
  // process fallback so a fresh open picks up a now-ready LLM result; skip once LLM/JSON is loaded.
  useEffect(() => {
    if (!floorPlanId) return;
    if (!doc || fileName === 'process-machine-map (regex)') {
      void handleLoadMindmap();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [floorPlanId]);

  const workCellOptions = useMemo(() => {
    if (!doc) return [] as string[];
    return collectWorkCells(doc.root);
  }, [doc]);

  const treeFilters = useMemo(
    () => ({
      query,
      types: filterTypes,
      workCell: filterWorkCell || null,
      onMapOnly: filterOnMapOnly,
    }),
    [query, filterTypes, filterWorkCell, filterOnMapOnly]
  );

  const filtersActive = hasMindmapFilters(treeFilters);

  const filteredRoot = useMemo(() => {
    if (!doc) return null;
    return filterTree(doc.root, treeFilters);
  }, [doc, treeFilters]);

  useEffect(() => {
    if (!doc) return;
    if (!filtersActive) {
      setExpanded(defaultExpandedIds(doc.root));
      return;
    }

    const filtered = filterTree(doc.root, treeFilters);
    if (!filtered) return;

    const next = new Set<string>();
    const walk = (n: ProcessMachineNode) => {
      if (!n.children?.length) return;
      next.add(n.id);
      if (n.type === 'tool_group') return;
      n.children.forEach(walk);
    };
    walk(filtered);
    setExpanded(next);
  }, [doc, treeFilters, filtersActive]);

  const clearFilters = useCallback(() => {
    setQuery('');
    setFilterTypes(new Set());
    setFilterWorkCell('');
    setFilterOnMapOnly(false);
  }, []);

  const toggleFilterType = useCallback((type: ProcessMachineNodeType) => {
    setFilterTypes((prev) => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type);
      else next.add(type);
      return next;
    });
  }, []);

  const selected = useMemo(() => {
    if (!doc || !selectedId) return null;
    return findNodeById(doc.root, selectedId);
  }, [doc, selectedId]);

  const selectNodeById = useCallback(
    (id: string) => {
      if (!doc) return;
      const node = findNodeById(doc.root, id);
      if (!node) return;
      applySelection(node, doc.root);
    },
    [applySelection, doc]
  );

  const toggleExpand = useCallback((id: string, e?: React.MouseEvent) => {
    e?.stopPropagation();
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const expandAll = useCallback(() => {
    if (!filteredRoot) return;
    const ids = collectExpandableIds(filteredRoot).filter((id) => {
      const n = findNodeById(filteredRoot, id);
      return n && n.type !== 'tool_group';
    });
    setExpanded(new Set([filteredRoot.id, ...ids]));
  }, [filteredRoot]);

  const collapseAll = useCallback(() => {
    if (!filteredRoot) return;
    setExpanded(new Set([filteredRoot.id]));
  }, [filteredRoot]);

  const closeDrawer = () => dispatch(setWorkspaceView('map'));

  if (drawerCollapsed) {
    return (
      <button
        type="button"
        onClick={() => setDrawerCollapsed(false)}
        title="Expand mind map"
        aria-label="Expand mind map"
        className="absolute top-3 right-3 z-30 inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-[11px] font-semibold tracking-wider text-slate-600 shadow-sm transition-colors hover:bg-slate-50 hover:text-slate-800"
      >
        Mind Map
        <ChevronLeftIcon />
      </button>
    );
  }

  const drawerShell = (body: React.ReactNode) => (
    <aside
      className={`absolute top-3 right-3 bottom-3 z-30 flex min-w-0 flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition-[width] duration-200 ${
        drawerExtended ? 'w-[min(48rem,58vw)]' : 'w-[32rem]'
      }`}
      aria-label="Mind map panel"
    >
      <div className="flex shrink-0 items-center justify-between gap-2 border-b border-slate-200 px-3 py-2">
        <div className="flex min-w-0 items-center gap-2">
          <button
            type="button"
            onClick={() => setDrawerCollapsed(true)}
            title="Collapse panel"
            aria-label="Collapse panel"
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
          >
            <ChevronRightIcon />
          </button>
          <div className="min-w-0">
            <div className="text-sm font-semibold text-slate-900">Mind Map</div>
            <div className="truncate text-[10px] text-slate-500">
              Select a node to highlight machines on the map
            </div>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-0.5">
          <button
            type="button"
            onClick={() => dispatch(toggleMindmapDrawerExtended())}
            title={drawerExtended ? 'Narrow panel' : 'Widen panel'}
            aria-label={drawerExtended ? 'Narrow panel' : 'Widen panel'}
            aria-pressed={drawerExtended}
            className={`flex h-7 w-7 items-center justify-center rounded-lg transition-colors ${
              drawerExtended
                ? 'bg-slate-100 text-slate-800'
                : 'text-slate-400 hover:bg-slate-100 hover:text-slate-600'
            }`}
          >
            <WidenIcon extended={drawerExtended} />
          </button>
          <button
            type="button"
            onClick={closeDrawer}
            title="Close mind map"
            aria-label="Close mind map"
            className="flex h-7 w-7 items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
          >
            <CloseIcon />
          </button>
        </div>
      </div>
      {doc && (
        <div className="flex shrink-0 gap-1 border-b border-slate-200 px-3 py-1.5">
          <button
            type="button"
            onClick={() => setDrawerTab('tree')}
            className={`rounded-md px-3 py-1.5 text-[11px] font-semibold transition-colors ${
              drawerTab === 'tree'
                ? 'bg-slate-900 text-white'
                : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
            }`}
          >
            Tree View
          </button>
          <button
            type="button"
            onClick={() => setDrawerTab('details')}
            className={`rounded-md px-3 py-1.5 text-[11px] font-semibold transition-colors ${
              drawerTab === 'details'
                ? 'bg-slate-900 text-white'
                : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
            }`}
          >
            Details
          </button>
        </div>
      )}
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">{body}</div>
    </aside>
  );

  if (!doc) {
    return drawerShell(
      <div className="flex flex-1 flex-col items-center justify-center gap-3 overflow-y-auto px-5 py-6 text-center">
        <div className="w-full rounded-xl border border-slate-200 bg-slate-50 p-5">
          <div className="text-sm font-semibold text-slate-900">Load process–machine map</div>
          <p className="mt-2 text-xs leading-5 text-slate-500">
            Load the categorized equipment for this floor plan from the backend.
          </p>

          <div className="mt-4 flex flex-col gap-2">
            <button
              type="button"
              disabled={busy || !canLoadFromApi}
              onClick={() => void handleLoadMindmap()}
              className="w-full rounded-md bg-blue-600 px-4 py-2.5 text-[12px] font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {loadingSource ? 'Loading…' : 'Load Mind Map'}
            </button>
          </div>

          {!canLoadFromApi && (
            <p className="mt-3 text-[11px] leading-5 text-slate-400">
              Open a floor plan from the server (with a floor_plan_id) to load the mind map.
            </p>
          )}

          {loadError && (
            <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-left text-[11px] text-amber-900">
              {loadError}
            </div>
          )}
        </div>
      </div>
    );
  }

  if (!filteredRoot) {
    return drawerShell(
      <div className="flex flex-1 flex-col items-center justify-center gap-3 px-6 text-center">
        <div className="text-sm font-medium text-slate-800">No matching items for these filters</div>
        <button
          type="button"
          onClick={clearFilters}
          className="text-[11px] font-semibold text-blue-600 hover:text-blue-700"
        >
          Clear filters
        </button>
      </div>
    );
  }

  const toolbar = (
    <div className="flex shrink-0 flex-col gap-2 border-b border-slate-200 px-3 py-2">
      <input
        type="search"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search process, tool group, or machine…"
        className="h-8 w-full rounded-md border border-slate-200 bg-slate-50 px-3 text-xs text-slate-800 outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400"
      />

      <div className="flex flex-wrap items-center gap-1">
        <span className="mr-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
          Type
        </span>
        {FILTER_TYPES.map((t) => {
          const active = filterTypes.has(t.id);
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => toggleFilterType(t.id)}
              aria-pressed={active}
              className={`rounded-full px-2.5 py-1 text-[10px] font-semibold transition-colors ${
                active
                  ? 'bg-slate-900 text-white'
                  : 'border border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
              }`}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        <label className="flex min-w-0 flex-1 items-center gap-1.5">
          <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
            Cell
          </span>
          <select
            value={filterWorkCell}
            onChange={(e) => setFilterWorkCell(e.target.value)}
            disabled={workCellOptions.length === 0}
            className="h-7 min-w-0 flex-1 rounded-md border border-slate-200 bg-white px-2 text-[11px] text-slate-700 outline-none focus:border-blue-400 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <option value="">All work cells</option>
            {workCellOptions.map((cell) => (
              <option key={cell} value={cell}>
                {cell}
              </option>
            ))}
          </select>
        </label>

        <button
          type="button"
          onClick={() => setFilterOnMapOnly((v) => !v)}
          aria-pressed={filterOnMapOnly}
          className={`rounded-full px-2.5 py-1 text-[10px] font-semibold transition-colors ${
            filterOnMapOnly
              ? 'bg-amber-500 text-white'
              : 'border border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
          }`}
          title="Show only machines that have floor-plan coordinates"
        >
          On map
        </button>

        {filtersActive && (
          <button
            type="button"
            onClick={clearFilters}
            className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[10px] font-semibold text-slate-600 hover:bg-slate-50"
          >
            Clear filters
          </button>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        <button
          type="button"
          onClick={expandAll}
          className="rounded-md border border-slate-200 bg-white px-2 py-1 text-[10px] font-medium text-slate-700 hover:bg-slate-50"
        >
          Expand
        </button>
        <button
          type="button"
          onClick={collapseAll}
          className="rounded-md border border-slate-200 bg-white px-2 py-1 text-[10px] font-medium text-slate-700 hover:bg-slate-50"
        >
          Collapse
        </button>
        <button
          type="button"
          disabled={busy || !canLoadFromApi}
          onClick={() => void handleLoadMindmap()}
          className="rounded-md border border-blue-200 bg-blue-50 px-2 py-1 text-[10px] font-medium text-blue-700 hover:bg-blue-100 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {loadingSource ? 'Loading…' : 'Reload'}
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() => dispatch(clearMindmapDocument())}
          className="rounded-md border border-slate-200 bg-white px-2 py-1 text-[10px] font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60"
        >
          Clear
        </button>
      </div>
      <div className="flex items-center gap-2 truncate text-[10px] text-slate-500">
        <span className="max-w-[180px] truncate" title={fileName ?? undefined}>
          {fileName ?? 'Mind map'}
        </span>
        <span className="shrink-0">
          {doc.summary.total_processes}p · {doc.summary.total_tool_groups}g ·{' '}
          {doc.summary.total_machines}m
        </span>
        {filtersActive && (
          <span className="shrink-0 rounded bg-slate-100 px-1.5 py-0.5 font-medium text-slate-600">
            Filtered
          </span>
        )}
      </div>
      {loadError && (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-2 py-1.5 text-left text-[10px] text-amber-900">
          {loadError}
        </div>
      )}
    </div>
  );

  const detailsPanel = (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-white">
      <div className="shrink-0 border-b border-slate-200 px-3 py-2">
        <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
          Selection
        </div>
        <div className="mt-0.5 truncate text-sm font-semibold text-slate-900">
          {selected ? selected.name : 'Nothing selected'}
        </div>
      </div>

      {selected ? (
        <div className="min-h-0 flex-1 overflow-y-auto px-3 py-3 text-xs text-slate-700">
          <dl className="space-y-1.5">
            <DetailRow label="Type" value={nodeTypeLabel(selected.type)} />
            <DetailRow label="ID" value={selected.id} mono />
            {selected.code && <DetailRow label="Code" value={selected.code} />}
            {typeof selected.count === 'number' && (
              <DetailRow label="Machines" value={String(selected.count)} />
            )}
            <DetailRow label="In subtree" value={String(relatedIds.length)} />
            {selected.work_cell && <DetailRow label="Work cell" value={selected.work_cell} />}
            {selected.equipment_type && (
              <DetailRow label="Equipment" value={selected.equipment_type} />
            )}
            {selected.tool_name && <DetailRow label="Tool name" value={selected.tool_name} />}
            {selected.tool_number && (
              <DetailRow label="Tool number" value={selected.tool_number} />
            )}
            {selected.layer && <DetailRow label="Layer" value={selected.layer} mono />}
            {selected.confidence && <DetailRow label="Confidence" value={selected.confidence} />}
            {selected.location && (
              <DetailRow
                label="Location"
                value={`${selected.location[0].toFixed(3)}, ${selected.location[1].toFixed(3)}`}
                mono
              />
            )}
            {selected.children && selected.children.length > 0 && (
              <DetailRow label="Children" value={String(selected.children.length)} />
            )}
          </dl>

          {selected.type === 'tool_group' && selected.children && selected.children.length > 0 && (
            <div className="mt-3">
              <div className="mb-1.5 flex items-center justify-between">
                <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                  Machines
                </div>
                <button
                  type="button"
                  onClick={(e) => toggleExpand(selected.id, e)}
                  className="text-[10px] font-medium text-blue-600 hover:text-blue-700"
                >
                  {expanded.has(selected.id) ? 'Collapse branch' : 'Expand branch'}
                </button>
              </div>
              <ul className="max-h-48 space-y-1 overflow-y-auto rounded-md border border-slate-200 bg-slate-50 p-1.5">
                {selected.children.slice(0, 200).map((m) => (
                  <li key={m.id}>
                    <button
                      type="button"
                      onClick={() => selectNodeById(m.id)}
                      className={`w-full rounded px-2 py-1 text-left text-[11px] hover:bg-white ${
                        selectedId === m.id
                          ? 'bg-amber-50 font-semibold text-amber-800'
                          : 'text-slate-700'
                      }`}
                    >
                      {m.name}
                      {m.work_cell ? (
                        <span className="ml-1 text-slate-400">· {m.work_cell}</span>
                      ) : null}
                    </button>
                  </li>
                ))}
                {selected.children.length > 200 && (
                  <li className="px-2 py-1 text-[10px] text-slate-400">
                    +{selected.children.length - 200} more — use search to narrow
                  </li>
                )}
              </ul>
            </div>
          )}
        </div>
      ) : (
        <div className="flex flex-1 items-center justify-center px-6 text-center text-xs text-slate-400">
          Select a node in Tree View to see its details
        </div>
      )}
    </div>
  );

  const treePanel = (
    <div className="min-h-0 flex-1 overflow-y-auto bg-gradient-to-b from-slate-50/80 to-white px-2.5 py-2.5">
      <div className="rounded-xl border border-slate-200/80 bg-white p-1.5 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
        <TreeMenuItem
          node={filteredRoot}
          depth={0}
          expanded={expanded}
          selectedId={selectedId}
          highlightNodeId={highlightNodeId}
          relatedSet={relatedSet}
          onSelect={selectNodeById}
          onToggle={toggleExpand}
        />
      </div>
      <p className="px-1 pt-2 text-center text-[10px] text-slate-400">
        Click a row to highlight machines on the map
      </p>
    </div>
  );

  return drawerShell(
    <>
      {toolbar}
      {drawerTab === 'details' ? detailsPanel : treePanel}
    </>
  );
};

function TreeMenuItem({
  node,
  depth,
  expanded,
  selectedId,
  highlightNodeId,
  relatedSet,
  onSelect,
  onToggle,
}: {
  node: ProcessMachineNode;
  depth: number;
  expanded: Set<string>;
  selectedId: string | null;
  highlightNodeId: string | null;
  relatedSet: Set<string>;
  onSelect: (id: string) => void;
  onToggle: (id: string, e?: React.MouseEvent) => void;
}) {
  const allChildren = node.children ?? [];
  const hasChildren = allChildren.length > 0;
  const isOpen = expanded.has(node.id);
  const isSelected = selectedId === node.id || highlightNodeId === node.id;
  const inSubtree = relatedSet.size > 0 && relatedSet.has(node.id) && !isSelected;

  const visibleChildren =
    node.type === 'tool_group' && allChildren.length > MACHINE_PREVIEW_LIMIT
      ? allChildren.slice(0, MACHINE_PREVIEW_LIMIT)
      : allChildren;
  const hiddenCount = allChildren.length - visibleChildren.length;

  const countLabel =
    typeof node.count === 'number'
      ? node.count
      : node.type === 'machine'
        ? null
        : allChildren.length || null;

  const meta = TYPE_META[node.type] ?? DEFAULT_TYPE_META;
  const nameSize =
    node.type === 'fab'
      ? 'text-[13px] font-semibold'
      : node.type === 'machine'
        ? 'text-[12px] font-medium'
        : 'text-[12.5px] font-semibold';

  const subtitle = node.work_cell || node.tool_number || node.code || null;

  return (
    <div className="relative">
      {depth > 0 && (
        <span
          className="pointer-events-none absolute bottom-0 top-0 w-px bg-slate-200"
          style={{ left: 10 + (depth - 1) * 16 + 11 }}
          aria-hidden
        />
      )}

      <div
        className={`relative mb-0.5 flex items-stretch overflow-hidden rounded-lg transition-colors ${
          isSelected
            ? 'bg-amber-50 shadow-sm ring-1 ring-amber-300/80'
            : inSubtree
              ? 'bg-amber-50/35'
              : 'hover:bg-slate-50'
        }`}
        style={{ marginLeft: depth * 16 }}
      >
        <span
          className={`w-1 shrink-0 self-stretch ${
            isSelected ? 'bg-amber-400' : meta.accent
          }`}
          aria-hidden
        />

        <div className="flex min-w-0 flex-1 items-center gap-1.5 py-1.5 pl-1.5 pr-2">
          {hasChildren ? (
            <button
              type="button"
              onClick={(e) => onToggle(node.id, e)}
              className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md transition-colors ${
                isOpen
                  ? 'bg-slate-100 text-slate-700'
                  : 'text-slate-400 hover:bg-slate-100 hover:text-slate-700'
              }`}
              aria-label={isOpen ? 'Collapse' : 'Expand'}
            >
              <TreeChevronIcon open={isOpen} />
            </button>
          ) : (
            <span className="flex h-7 w-7 shrink-0 items-center justify-center">
              <span className="h-1.5 w-1.5 rounded-full bg-slate-300" />
            </span>
          )}

          <button
            type="button"
            onClick={() => onSelect(node.id)}
            className="flex min-w-0 flex-1 items-center gap-2.5 text-left"
            title={node.name}
          >
            <span
              className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-[10px] font-bold tracking-wide ${meta.iconBg}`}
              aria-hidden
            >
              {meta.icon}
            </span>

            <span className="min-w-0 flex-1">
              <span
                className={`block truncate leading-snug ${nameSize} ${
                  isSelected ? 'text-amber-950' : 'text-slate-800'
                }`}
              >
                {node.name}
              </span>
              {subtitle && (
                <span className="mt-0.5 block truncate text-[10px] leading-tight text-slate-400">
                  {node.work_cell
                    ? `Work cell · ${node.work_cell}`
                    : node.tool_number
                      ? `Tool · ${node.tool_number}`
                      : node.code
                        ? node.code
                        : subtitle}
                </span>
              )}
            </span>

            <span className="flex shrink-0 items-center gap-1.5">
              <span
                className={`rounded-md px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide ${meta.badge}`}
              >
                {nodeTypeLabel(node.type)}
              </span>
              {countLabel != null && (
                <span
                  className={`rounded-full px-2 py-0.5 text-[10px] font-semibold tabular-nums ${
                    isSelected
                      ? 'bg-amber-200/70 text-amber-900'
                      : 'bg-slate-100 text-slate-600'
                  }`}
                >
                  {countLabel}
                </span>
              )}
            </span>
          </button>
        </div>
      </div>

      {hasChildren && isOpen && (
        <div className="pb-0.5">
          {visibleChildren.map((child) => (
            <TreeMenuItem
              key={child.id}
              node={child}
              depth={depth + 1}
              expanded={expanded}
              selectedId={selectedId}
              highlightNodeId={highlightNodeId}
              relatedSet={relatedSet}
              onSelect={onSelect}
              onToggle={onToggle}
            />
          ))}
          {hiddenCount > 0 && (
            <div
              className="mb-1 rounded-md px-2 py-1.5 text-[10px] text-slate-400"
              style={{ marginLeft: (depth + 1) * 16 + 8 }}
            >
              +{hiddenCount} more — search to find a specific machine
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function TreeChevronIcon({ open }: { open: boolean }) {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      className={`transition-transform duration-150 ${open ? 'rotate-90' : ''}`}
      aria-hidden
    >
      <path d="M9 6l6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function DetailRow({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div className="flex gap-3">
      <dt className="w-20 shrink-0 text-slate-400">{label}</dt>
      <dd className={`min-w-0 break-all ${mono ? 'font-mono text-[10px]' : ''}`}>{value}</dd>
    </div>
  );
}

function ChevronLeftIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M15 18l-6-6 6-6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ChevronRightIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M9 18l6-6-6-6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function WidenIcon({ extended }: { extended: boolean }) {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      {extended ? (
        <>
          <path d="M8 3v3a2 2 0 0 1-2 2H3" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M21 8h-3a2 2 0 0 1-2-2V3" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M3 16h3a2 2 0 0 1 2 2v3" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M16 21v-3a2 2 0 0 1 2-2h3" strokeLinecap="round" strokeLinejoin="round" />
        </>
      ) : (
        <>
          <path d="M15 3h6v6" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M9 21H3v-6" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M21 3l-7 7" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M3 21l7-7" strokeLinecap="round" strokeLinejoin="round" />
        </>
      )}
    </svg>
  );
}

export default ProcessMachineMindmap;
